import React from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Avatar, Box, ButtonBase, Divider, Drawer, IconButton, ListItemIcon, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import SearchIcon from "@mui/icons-material/Search";
import { useAuth } from "../../common/auth";
import { ROUTES } from "../../common/constant/routes";
import { C, DISPLAY_FONT } from "../../common/theme";
import { ToastProvider } from "../../ui/Toast";
import { PayoutKeyProvider } from "../PayoutKey";
import { useIsNarrow } from "../../ui/hooks";
import CommandPalette from "./CommandPalette";
import { CountsProvider, useCounts } from "./counts";
import { BadgeTone, NAV_SECTIONS, NavItem } from "./nav";

const WIDE = 232;
const COMPACT = 76;

const BADGE: Record<BadgeTone, { bg: string; fg: string }> = {
  attention: { bg: C.coral, fg: C.ink },
  danger: { bg: "#F3B6C1", fg: "#5A0014" },
  quiet: { bg: "#333333", fg: C.sideText },
};

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

function Logo({ compact }: { compact: boolean }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: compact ? 0 : 1, justifyContent: compact ? "center" : "flex-start", minHeight: 44 }}>
      <Box sx={{ width: 34, height: 34, borderRadius: "8px", bgcolor: "#fff", display: "grid", placeItems: "center", flex: "none", overflow: "hidden" }}>
        <Box component="img" src="/logo.png" alt="" sx={{ width: 30, height: 30, objectFit: "contain" }} />
      </Box>
      {!compact && (
        <Box sx={{ lineHeight: 1 }}>
          <Typography sx={{ fontFamily: DISPLAY_FONT, fontWeight: 800, fontSize: 18, letterSpacing: "-0.03em", color: "#fff", lineHeight: 1.1 }}>Inflos</Typography>
          <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#8C857E" }}>Admin</Typography>
        </Box>
      )}
    </Box>
  );
}

function Sidebar({ compact, onNavigate, onSearch }: { compact: boolean; onNavigate?: () => void; onSearch: () => void }) {
  const { adminUser, signOutUser, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const counts = useCounts().data;

  const canSee = (item: NavItem) => {
    if (item.superadminOnly) return adminUser?.role === "superadmin";
    if (!item.permission) return true;
    const anyOf = Array.isArray(item.permission) ? item.permission : [item.permission];
    return anyOf.some((key) => hasPermission(key));
  };
  const sections = NAV_SECTIONS.map((s) => ({ ...s, items: s.items.filter(canSee) })).filter((s) => s.items.length);
  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", bgcolor: C.side, color: C.sideText, px: compact ? 1 : 1.5, py: 2, boxSizing: "border-box" }}>
      <Logo compact={compact} />
      <Tooltip title={compact ? `Search (${isMac ? "⌘" : "Ctrl+"}K)` : ""} placement="right">
        <ButtonBase
          onClick={onSearch}
          aria-label="Search"
          sx={{
            mt: 2,
            mb: 1,
            minHeight: 40,
            borderRadius: "6px",
            border: "1px solid #2E2E2E",
            bgcolor: "#171717",
            color: "#9C958E",
            justifyContent: compact ? "center" : "flex-start",
            gap: 1,
            px: compact ? 0 : 1.25,
            fontSize: 13.5,
            "&:hover": { borderColor: "#444", color: "#fff" },
            "&:focus-visible": { outline: `2px solid ${C.coral}` },
          }}
        >
          <SearchIcon sx={{ fontSize: 18 }} />
          {!compact && (
            <>
              <Box component="span" sx={{ flex: 1, textAlign: "left" }}>
                Search
              </Box>
              <Box component="span" sx={{ fontSize: 11, fontWeight: 700, border: "1px solid #3A3A3A", borderRadius: "4px", px: 0.6, py: 0.1 }}>
                {isMac ? "⌘K" : "Ctrl K"}
              </Box>
            </>
          )}
        </ButtonBase>
      </Tooltip>

      <Box component="nav" aria-label="Main" sx={{ flex: 1, overflowY: "auto", mx: compact ? -1 : -1.5, px: compact ? 1 : 1.5 }}>
        {sections.map((section, index) => (
          <Box key={section.title ?? `s${index}`} sx={{ mt: index ? 2 : 1 }}>
            {section.title &&
              (compact ? (
                <Divider sx={{ borderColor: "#262626", mb: 1, mx: 1 }} />
              ) : (
                <Typography sx={{ px: 1.25, pb: 0.5, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#7A736C" }}>
                  {section.title}
                </Typography>
              ))}
            {section.items.map((item) => {
              const active = isActive(item.path);
              const badge = counts && item.badge ? item.badge(counts) : null;
              const tone = badge ? BADGE[badge.tone] : null;
              const button = (
                <ButtonBase
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    onNavigate?.();
                  }}
                  aria-current={active ? "page" : undefined}
                  aria-label={compact ? `${item.label}${badge ? `, ${badge.label}` : ""}` : undefined}
                  sx={{
                    position: "relative",
                    width: "100%",
                    minHeight: 42,
                    mb: 0.25,
                    borderRadius: "6px",
                    justifyContent: compact ? "center" : "flex-start",
                    gap: 1.25,
                    px: compact ? 0 : 1.25,
                    color: active ? "#fff" : C.sideText,
                    bgcolor: active ? C.sideActive : "transparent",
                    fontSize: 14,
                    fontWeight: active ? 700 : 600,
                    "& svg": { fontSize: 20, color: active ? "#fff" : "#9C958E" },
                    "&:hover": { bgcolor: active ? C.sideActive : "#1A1A1A", color: "#fff" },
                    "&:focus-visible": { outline: `2px solid ${C.coral}`, outlineOffset: -2 },
                  }}
                >
                  {item.icon}
                  {!compact && (
                    <Box component="span" sx={{ flex: 1, textAlign: "left", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {item.label}
                    </Box>
                  )}
                  {badge && tone && !compact && (
                    <Box component="span" title={badge.label} sx={{ minWidth: 22, textAlign: "center", fontSize: 11.5, fontWeight: 800, px: 0.75, py: 0.1, borderRadius: "5px", bgcolor: tone.bg, color: tone.fg }}>
                      {badge.value > 99 ? "99+" : badge.value}
                    </Box>
                  )}
                  {badge && tone && compact && (
                    <Box component="span" sx={{ position: "absolute", top: 7, right: 14, width: 9, height: 9, borderRadius: "50%", bgcolor: tone.bg, border: `2px solid ${C.side}` }} />
                  )}
                </ButtonBase>
              );
              return compact ? (
                <Tooltip key={item.path} title={badge ? `${item.label} · ${badge.label}` : item.label} placement="right">
                  {button}
                </Tooltip>
              ) : (
                button
              );
            })}
          </Box>
        ))}
      </Box>

      <Divider sx={{ borderColor: "#262626", my: 1.5 }} />
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexDirection: compact ? "column" : "row", px: compact ? 0 : 0.5 }}>
        <Avatar src={adminUser?.profile_image || undefined} alt="" sx={{ width: 32, height: 32, fontSize: 13 }}>
          {(adminUser?.display_name || adminUser?.email || "?")[0]?.toUpperCase()}
        </Avatar>
        {!compact && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>
              {adminUser?.display_name || adminUser?.email}
            </Typography>
            <Typography noWrap sx={{ fontSize: 11.5, color: "#8C857E", textTransform: "capitalize" }}>
              {adminUser?.role}
            </Typography>
          </Box>
        )}
        <Tooltip title="Sign out" placement={compact ? "right" : "top"}>
          <IconButton
            aria-label="Sign out"
            onClick={async () => {
              await signOutUser();
              navigate(ROUTES.LOGIN);
            }}
            sx={{ color: "#9C958E", "&:hover": { color: "#fff", bgcolor: "#1A1A1A" } }}
          >
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

function TopBar({ onMenu, onSearch, showMenu }: { onMenu: () => void; onSearch: () => void; showMenu: boolean }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { adminUser, signOutUser } = useAuth();
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const section = NAV_SECTIONS.find((s) => s.items.some((i) => location.pathname.startsWith(i.path)));
  const item = section?.items.find((i) => location.pathname.startsWith(i.path));

  return (
    <Box
      component="header"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 20,
        display: "flex",
        alignItems: "center",
        gap: 1,
        minHeight: 56,
        px: { xs: 1, md: 3, lg: 4 },
        pt: "env(safe-area-inset-top, 0px)",
        bgcolor: "rgba(246,244,242,0.88)",
        backdropFilter: "saturate(1.4) blur(10px)",
        borderBottom: `1px solid ${C.line}`,
      }}
    >
      {showMenu && (
        <IconButton aria-label="Open menu" onClick={onMenu} sx={{ width: 44, height: 44 }}>
          <MenuIcon />
        </IconButton>
      )}
      <Box component="nav" aria-label="Breadcrumb" sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 0.75, fontSize: 13.5 }}>
        {section?.title && section.title !== item?.label && (
          <Box component="span" sx={{ color: C.muted, display: { xs: "none", sm: "inline" } }}>
            {section.title} <Box component="span" sx={{ mx: 0.5, color: C.faint }}>/</Box>
          </Box>
        )}
        <Box component="span" sx={{ fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {item?.label ?? "Inflos Admin"}
        </Box>
      </Box>
      <ButtonBase
        onClick={onSearch}
        aria-label="Search orders, users and payouts"
        sx={{
          display: { xs: "none", sm: "flex" },
          alignItems: "center",
          gap: 1,
          minHeight: 38,
          width: { sm: 240, lg: 320 },
          px: 1.25,
          borderRadius: "6px",
          border: `1px solid ${C.line2}`,
          bgcolor: C.paper,
          color: C.muted,
          fontSize: 13.5,
          justifyContent: "flex-start",
          "&:hover": { borderColor: C.ink },
          "&:focus-visible": { outline: `2px solid ${C.coral}`, outlineOffset: 2 },
        }}
      >
        <SearchIcon sx={{ fontSize: 18 }} />
        <Box component="span" sx={{ flex: 1, textAlign: "left" }}>
          Search orders, users, payouts
        </Box>
        <Box component="span" sx={{ fontSize: 11, fontWeight: 700, border: `1px solid ${C.line2}`, borderRadius: "4px", px: 0.6 }}>
          {isMac ? "⌘K" : "Ctrl K"}
        </Box>
      </ButtonBase>
      <IconButton aria-label="Search" onClick={onSearch} sx={{ display: { xs: "inline-flex", sm: "none" }, width: 44, height: 44 }}>
        <SearchIcon />
      </IconButton>
      <IconButton aria-label="Account" onClick={(e) => setAnchor(e.currentTarget)} sx={{ width: 44, height: 44, display: { md: "none" } }}>
        <Avatar src={adminUser?.profile_image || undefined} alt="" sx={{ width: 30, height: 30, fontSize: 12 }}>
          {(adminUser?.display_name || adminUser?.email || "?")[0]?.toUpperCase()}
        </Avatar>
      </IconButton>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {adminUser?.display_name || adminUser?.email}
          </Typography>
          <Typography variant="caption" sx={{ color: C.muted, textTransform: "capitalize" }}>
            {adminUser?.role}
          </Typography>
        </Box>
        <Divider />
        <MenuItem
          onClick={async () => {
            setAnchor(null);
            await signOutUser();
            navigate(ROUTES.LOGIN);
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>
    </Box>
  );
}

function Shell() {
  const { adminUser, hasPermission } = useAuth();
  const isMobile = useIsNarrow(900);
  const compact = useIsNarrow(1200) && !isMobile;
  const [drawer, setDrawer] = React.useState(false);
  const [search, setSearch] = React.useState(false);
  const location = useLocation();
  const width = isMobile ? 0 : compact ? COMPACT : WIDE;

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearch((s) => !s);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Each page starts at the top.
  // Braces matter: Chrome's scrollTo now returns a Promise, and an effect
  // that returns one crashes React on the next navigation ("c is not a function").
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const pages = NAV_SECTIONS.flatMap((s) => s.items).filter((item) => {
    if (item.superadminOnly) return adminUser?.role === "superadmin";
    if (!item.permission) return true;
    return (Array.isArray(item.permission) ? item.permission : [item.permission]).some((k) => hasPermission(k));
  });

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: C.ground }}>
      {isMobile ? (
        <Drawer open={drawer} onClose={() => setDrawer(false)} PaperProps={{ sx: { width: 280, maxWidth: "85vw", bgcolor: C.side, border: 0 } }}>
          <Sidebar compact={false} onNavigate={() => setDrawer(false)} onSearch={() => { setDrawer(false); setSearch(true); }} />
        </Drawer>
      ) : (
        <Box component="aside" sx={{ position: "fixed", inset: "0 auto 0 0", width, zIndex: 30 }}>
          <Sidebar compact={compact} onSearch={() => setSearch(true)} />
        </Box>
      )}
      <Box sx={{ ml: `${width}px`, minWidth: 0, display: "flex", flexDirection: "column", minHeight: "100vh" }}>
        <TopBar showMenu={isMobile} onMenu={() => setDrawer(true)} onSearch={() => setSearch(true)} />
        <Box component="main" sx={{ flex: 1, minWidth: 0 }}>
          <Outlet />
        </Box>
      </Box>
      <CommandPalette open={search} onClose={() => setSearch(false)} pages={pages} />
    </Box>
  );
}

/** Ink sidebar (same groups as before) with live badges, a top bar with the
 * breadcrumb and ⌘K search, and the page below. Sidebar shrinks to icons
 * between 900 and 1200px and becomes a drawer on phones. */
export default function AppLayout() {
  return (
    <ToastProvider>
      <CountsProvider>
        <PayoutKeyProvider>
          <Shell />
        </PayoutKeyProvider>
      </CountsProvider>
    </ToastProvider>
  );
}
